<template>
  <div class="logs-visao-geral">
    <FiltrosTabLogs
      :filtros="filtros"
      :equipes="equipes"
      :profissionais="profissionais"
      :funcionalidades="funcionalidades"
      :loading-equipes="loadingEquipes"
      :loading-profissionais="loadingProfissionais"
      :loading-funcionalidades="loadingFuncionalidades"
      @alterar="alterarFiltros"
    />

    <!-- Loading -->
    <div v-if="loading" class="logs-loading-geral">
      <b-spinner label="Carregando..." role="status" />
    </div>

    <!-- Erro -->
    <div v-else-if="erro" class="logs-erro-geral">
      <i class="icofont-warning-alt"></i>

      <strong> Não foi possível carregar os dados </strong>

      <span> Tente novamente em alguns instantes. </span>
    </div>

    <!-- Conteúdo -->
    <div v-else>
      <!-- KPIs -->
      <div class="logs-kpis">
        <div class="logs-kpi">
          <div class="logs-kpi-label">Acessos funcionais</div>

          <div class="logs-kpi-value">
            {{ formatarNumero(kpis.acessos_funcionais) }}
          </div>
        </div>

        <div class="logs-kpi">
          <div class="logs-kpi-label">INEs ativos</div>

          <div class="logs-kpi-value">
            {{ formatarNumero(kpis.ines_ativos) }}
          </div>
        </div>

        <div class="logs-kpi">
          <div class="logs-kpi-label">Profissionais ativos</div>

          <div class="logs-kpi-value">
            {{ formatarNumero(kpis.profissionais_ativos) }}
          </div>
        </div>

        <div class="logs-kpi">
          <div class="logs-kpi-label">Funcionalidades usadas</div>

          <div class="logs-kpi-value">
            {{ formatarNumero(kpis.funcionalidades_usadas) }}
          </div>
        </div>
      </div>

      <!-- Gráfico e ranking -->
      <div class="row">
        <div class="col-lg-8">
          <div class="logs-card">
            <div class="logs-card-title">
              <div>
                <h4>Evolução de utilização</h4>

                <p>Acessos funcionais e profissionais ativos por dia</p>
              </div>

              <div class="chart-legend">
                <span>
                  <i class="chart-dot acessos"></i>
                  Acessos
                </span>

                <span>
                  <i class="chart-dot profissionais"></i>
                  Profissionais
                </span>
              </div>
            </div>

            <div class="logs-chart">
              <apexchart
                type="line"
                height="300"
                :options="graficoOptions"
                :series="graficoSeries"
              />
            </div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="logs-card">
            <div class="logs-card-title">
              <div>
                <h4>Funcionalidades mais utilizadas</h4>

                <p>Ranking no período selecionado</p>
              </div>

              <span class="label label-default"> Top 8 </span>
            </div>

            <div
              v-if="funcionalidadesMaisUtilizadas.length"
              class="ranking-list"
            >
              <div
                v-for="funcionalidade in funcionalidadesMaisUtilizadas"
                :key="funcionalidade.id || funcionalidade.card"
                class="ranking-item"
              >
                <div class="ranking-label" :title="funcionalidade.card">
                  {{ funcionalidade.card }}
                </div>

                <div class="ranking-progress">
                  <div
                    class="ranking-progress-bar"
                    :style="{
                      width: percentualFuncionalidade(funcionalidade.total),
                    }"
                  ></div>
                </div>

                <div class="ranking-value">
                  {{ formatarNumero(funcionalidade.total) }}
                </div>
              </div>
            </div>

            <div v-else class="text-muted text-center py-4">
              Nenhum acesso encontrado no período.
            </div>
          </div>
        </div>
      </div>

      <!-- Adoção / recorrência / atenção -->
      <div class="row">
        <div class="col-lg-4">
          <div class="logs-card">
            <div class="logs-card-title">
              <div>
                <h4>Adoção por equipe</h4>

                <p>Utilização das equipes no período</p>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-logs">
                <thead>
                  <tr>
                    <th>INE</th>
                    <th>Acessos</th>
                    <th>Prof.</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="equipe in adocaoPorEquipe" :key="equipe.ine">
                    <td>
                      <strong>
                        {{ equipe.ine }}
                      </strong>
                    </td>

                    <td>
                      {{ formatarNumero(equipe.acessos) }}
                    </td>

                    <td>
                      {{ formatarNumero(equipe.profissionais) }}
                    </td>
                  </tr>

                  <tr v-if="!adocaoPorEquipe.length">
                    <td colspan="3" class="text-center text-muted py-4">
                      Nenhum acesso encontrado no período.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="logs-card">
            <div class="logs-card-title">
              <div>
                <h4>Recorrência dos profissionais</h4>

                <p>Distribuição por dias ativos no período</p>
              </div>
            </div>

            <div class="logs-list">
              <div class="logs-list-item">
                <div>
                  <strong>Alta recorrência</strong>

                  <small> 10+ dias ativos </small>
                </div>

                <span class="logs-badge success">
                  {{ formatarNumero(recorrencia.alta_recorrencia) }}
                </span>
              </div>

              <div class="logs-list-item">
                <div>
                  <strong>Recorrentes</strong>

                  <small> 5 a 9 dias ativos </small>
                </div>

                <span class="logs-badge success">
                  {{ formatarNumero(recorrencia.recorrentes) }}
                </span>
              </div>

              <div class="logs-list-item">
                <div>
                  <strong>Baixa recorrência</strong>

                  <small> 2 a 4 dias ativos </small>
                </div>

                <span class="logs-badge warning">
                  {{ formatarNumero(recorrencia.baixa_recorrencia) }}
                </span>
              </div>

              <div class="logs-list-item">
                <div>
                  <strong>Uso eventual</strong>

                  <small> 1 dia ativo </small>
                </div>

                <span class="logs-badge danger">
                  {{ formatarNumero(recorrencia.uso_eventual) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="logs-card">
            <div class="logs-card-title">
              <div>
                <h4>Atenção</h4>

                <p>Oportunidades para acompanhamento</p>
              </div>
            </div>

            <div class="logs-list">
              <div class="logs-list-item">
                <div>
                  <strong>
                    {{ formatarNumero(atencao.ines_sem_utilizacao) }}
                    INEs
                  </strong>

                  <small> Sem utilização no período </small>
                </div>

                <span class="logs-badge warning"> Atenção </span>
              </div>

              <div class="logs-list-item">
                <div>
                  <strong>
                    {{
                      formatarNumero(atencao.funcionalidades_baixa_utilizacao)
                    }}
                    funcionalidades
                  </strong>

                  <small> Baixa utilização </small>
                </div>

                <span class="logs-badge warning"> Atenção </span>
              </div>

              <div class="logs-list-item">
                <div>
                  <strong>
                    {{ formatarNumero(atencao.logs_ine_invalido) }}
                    registros
                  </strong>

                  <small> INE ausente no log </small>
                </div>

                <span class="logs-badge secondary"> Qualidade </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import FiltrosTabLogs from '../components/FiltrosTabLogs'

export default {
  name: 'VisaoGeral',
  components: {
    apexchart: VueApexCharts,
    FiltrosTabLogs,
  },
  props: {
    filtrosPeriodo: {
      type: Object,
      required: true,
    },

    equipes: {
      type: Array,
      default: () => [],
    },

    profissionais: {
      type: Array,
      default: () => [],
    },

    funcionalidades: {
      type: Array,
      default: () => [],
    },

    loadingEquipes: {
      type: Boolean,
      default: false,
    },

    loadingProfissionais: {
      type: Boolean,
      default: false,
    },

    loadingFuncionalidades: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filtros: {
        ine: '',
        profissional: '',
        funcionalidade: '',
      },
      loading: false,
      erro: false,
      kpis: {
        acessos_funcionais: 0,
        ines_ativos: 0,
        profissionais_ativos: 0,
        funcionalidades_usadas: 0,
      },
      evolucaoUtilizacao: [],
      funcionalidadesMaisUtilizadas: [],
      adocaoPorEquipe: [],
      recorrencia: {
        alta_recorrencia: 0,
        recorrentes: 0,
        baixa_recorrencia: 0,
        uso_eventual: 0,
      },
      atencao: {
        ines_sem_utilizacao: 0,
        funcionalidades_baixa_utilizacao: 0,
        logs_ine_invalido: 0,
      },
    }
  },
  computed: {
    /**
     * Retorna os filtros utilizados na consulta da visão geral.
     *
     * @returns {Object}
     */
    filtrosApi() {
      return {
        ...this.filtrosPeriodo,
        ...this.filtros,
      }
    },

    /**
     * Retorna o maior número de acessos entre as funcionalidades
     * utilizadas no período.
     *
     * @returns {Number}
     */
    maiorValorFuncionalidades() {
      if (!this.funcionalidadesMaisUtilizadas.length) {
        return 0
      }

      return Math.max(
        ...this.funcionalidadesMaisUtilizadas.map((item) =>
          Number(item.total || 0)
        )
      )
    },

    /**
     * Retorna as séries utilizadas no gráfico de evolução.
     *
     * @returns {Array}
     */
    graficoSeries() {
      return [
        {
          name: 'Acessos',
          data: this.evolucaoUtilizacao.map((item) =>
            Number(item.acessos || 0)
          ),
        },
        {
          name: 'Profissionais',
          data: this.evolucaoUtilizacao.map((item) =>
            Number(item.profissionais || 0)
          ),
        },
      ]
    },

    /**
     * Retorna as configurações do gráfico de evolução.
     *
     * @returns {Object}
     */
    graficoOptions() {
      return {
        chart: {
          type: 'line',
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },

        stroke: {
          curve: 'smooth',
          width: 2,
        },

        markers: {
          size: 3,
          hover: {
            size: 5,
          },
        },

        dataLabels: {
          enabled: false,
        },

        legend: {
          show: false,
        },

        xaxis: {
          categories: this.evolucaoUtilizacao.map((item) =>
            this.formatarDataGrafico(item.data)
          ),

          labels: {
            rotate: 0,
          },

          axisBorder: {
            show: false,
          },

          axisTicks: {
            show: false,
          },
        },

        yaxis: {
          min: 0,

          labels: {
            formatter: (valor) => {
              return Math.round(valor)
            },
          },
        },

        grid: {
          xaxis: {
            lines: {
              show: false,
            },
          },
        },

        tooltip: {
          shared: true,
          intersect: false,
        },

        noData: {
          text: 'Nenhum acesso encontrado no período.',
        },
      }
    },
  },
  watch: {
    filtrosPeriodo: {
      deep: true,

      /**
       * Recarrega os dados quando o período aplicado é alterado.
       *
       * @returns {void}
       */
      handler() {
        this.filtros.profissional = ''

        this.carregarDados()
      },
    },
  },
  mounted() {
    this.carregarDados()
  },
  methods: {
    /**
     * Atualiza os filtros específicos da visão geral
     * e recarrega os dados.
     *
     * @param {Object} filtros Filtros selecionados
     * @param {String} campo Campo que originou a alteração
     * @returns {void}
     */
    alterarFiltros(filtros, campo) {
      this.filtros = {
        ...filtros,
      }

      this.carregarDados()
    },

    /**
     * Carrega os dados da visão geral de acordo com os filtros selecionados.
     *
     * @returns {Promise<void>}
     */
    async carregarDados() {
      this.loading = true
      this.erro = false

      try {
        const { data } = await this.$api.get(
          `/app/logs/${this.$store.state.cod_ibge}/visao-geral`,
          {
            params: this.filtrosApi,
          }
        )

        this.preencherDados(data.data || data)
      } catch (error) {
        console.error('Erro ao carregar visão geral dos logs:', error)

        this.resetarDados()
        this.erro = true
      } finally {
        this.loading = false
      }
    },

    /**
     * Preenche os dados da visão geral retornados pela API.
     *
     * @param {Object} dados Dados retornados pela API
     * @returns {void}
     */
    preencherDados(dados) {
      if (!dados) {
        this.resetarDados()

        return
      }

      this.kpis = {
        acessos_funcionais: Number(dados.kpis?.acessos_funcionais || 0),
        ines_ativos: Number(dados.kpis?.ines_ativos || 0),
        profissionais_ativos: Number(dados.kpis?.profissionais_ativos || 0),
        funcionalidades_usadas: Number(dados.kpis?.funcionalidades_usadas || 0),
      }

      this.evolucaoUtilizacao = Array.isArray(dados.evolucaoUtilizacao)
        ? dados.evolucaoUtilizacao
        : []

      this.funcionalidadesMaisUtilizadas = Array.isArray(
        dados.funcionalidadesMaisUtilizadas
      )
        ? dados.funcionalidadesMaisUtilizadas
        : []

      this.adocaoPorEquipe = Array.isArray(dados.adocaoPorEquipe)
        ? dados.adocaoPorEquipe
        : []

      this.recorrencia = {
        alta_recorrencia: Number(
          dados.recorrenciaProfissionais?.alta_recorrencia || 0
        ),
        recorrentes: Number(dados.recorrenciaProfissionais?.recorrentes || 0),
        baixa_recorrencia: Number(
          dados.recorrenciaProfissionais?.baixa_recorrencia || 0
        ),
        uso_eventual: Number(dados.recorrenciaProfissionais?.uso_eventual || 0),
      }

      this.atencao = {
        ines_sem_utilizacao: Number(dados.atencao?.ines_sem_utilizacao || 0),
        funcionalidades_baixa_utilizacao: Number(
          dados.atencao?.funcionalidades_baixa_utilizacao || 0
        ),
        logs_ine_invalido: Number(dados.atencao?.logs_ine_invalido || 0),
      }
    },

    /**
     * Restaura os dados da visão geral para os valores iniciais.
     *
     * @returns {void}
     */
    resetarDados() {
      this.kpis = {
        acessos_funcionais: 0,
        ines_ativos: 0,
        profissionais_ativos: 0,
        funcionalidades_usadas: 0,
      }

      this.evolucaoUtilizacao = []
      this.funcionalidadesMaisUtilizadas = []
      this.adocaoPorEquipe = []

      this.recorrencia = {
        alta_recorrencia: 0,
        recorrentes: 0,
        baixa_recorrencia: 0,
        uso_eventual: 0,
      }

      this.atencao = {
        ines_sem_utilizacao: 0,
        funcionalidades_baixa_utilizacao: 0,
        logs_ine_invalido: 0,
      }
    },

    /**
     * Formata a data utilizada no eixo horizontal do gráfico.
     *
     * @param {String} data Data no formato YYYY-MM-DD
     * @returns {String}
     */
    formatarDataGrafico(data) {
      if (!data) {
        return ''
      }

      const partes = data.split('-')

      if (partes.length !== 3) {
        return data
      }

      return `${partes[2]}/${partes[1]}`
    },

    /**
     * Calcula o percentual de utilização da funcionalidade
     * em relação à funcionalidade mais utilizada.
     *
     * @param {Number|String} total Total de acessos
     * @returns {String}
     */
    percentualFuncionalidade(total) {
      const valor = Number(total || 0)

      if (!this.maiorValorFuncionalidades) {
        return '0%'
      }

      const percentual = (valor / this.maiorValorFuncionalidades) * 100

      return `${percentual}%`
    },

    /**
     * Formata um valor numérico para exibição.
     *
     * @param {Number|String|null} valor Valor a ser formatado
     * @returns {String}
     */
    formatarNumero(valor) {
      return Number(valor || 0).toLocaleString('pt-BR')
    },
  },
}
</script>
